import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { notesApi } from '@/services/notes';
import { HandwrittenNoteRequest } from '@/types';
import toast from 'react-hot-toast';

interface HandwrittenNotesProps {
  meetingId: number;
  meetingDate: string;
  meetingTime: string;
  location: string;
  onComplete?: (noteId: number) => void;
}

export const HandwrittenNotes = ({
  meetingId,
  onComplete,
}: HandwrittenNotesProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik obrazu');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      // Remove data:image/jpeg;base64, prefix
      const base64 = result.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      console.log('🎥 Starting camera...');

      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Twoja przeglądarka nie obsługuje dostępu do kamery');
        console.error('MediaDevices API not available');
        return;
      }

      // Check camera permission status (if supported)
      try {
        if ('permissions' in navigator) {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('📋 Camera permission status:', permissionStatus.state);

          if (permissionStatus.state === 'denied') {
            toast.error(
              'Dostęp do kamery jest zablokowany. Włącz uprawnienia w ustawieniach przeglądarki: Ustawienia → Prywatność i bezpieczeństwo → Uprawnienia witryn → Kamera',
              { duration: 8000 }
            );
            return;
          }
        }
      } catch (permError) {
        console.log('⚠️ Permission API not fully supported, continuing anyway');
      }

      // Request camera access with simplified constraints for better Android compatibility
      console.log('📸 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          // Simplified constraints - let device choose optimal resolution
        }
      });

      console.log('✅ Camera stream obtained:', stream.getVideoTracks());

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraActive(true);

        // Wait for video metadata to load with timeout
        console.log('⏳ Waiting for video metadata...');
        const metadataPromise = new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              console.log('✅ Metadata loaded');
              resolve();
            };
          } else {
            resolve();
          }
        });

        const timeoutPromise = new Promise<void>((resolve) =>
          setTimeout(() => {
            console.log('⏰ Metadata timeout - continuing anyway');
            resolve();
          }, 3000)
        );

        await Promise.race([metadataPromise, timeoutPromise]);

        // Explicitly play the video (required for some mobile browsers)
        if (videoRef.current) {
          try {
            console.log('▶️ Playing video...');
            await videoRef.current.play();
            console.log('✅ Video playing');
            toast.success('Kamera uruchomiona!');
          } catch (playError) {
            console.error('Video play error:', playError);
            toast.error('Nie można uruchomić podglądu kamery');
          }
        }
      }
    } catch (error: any) {
      console.error('❌ Camera error:', error);

      // Better error messages based on error type
      if (error.name === 'NotAllowedError') {
        toast.error(
          'Dostęp do kamery został odrzucony. Aby włączyć: Ustawienia przeglądarki → Uprawnienia witryn → Kamera → Zezwól',
          { duration: 8000 }
        );
      } else if (error.name === 'NotFoundError') {
        toast.error('Nie znaleziono kamery na urządzeniu');
      } else if (error.name === 'NotReadableError') {
        toast.error('Kamera jest używana przez inną aplikację');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Nie można spełnić wymagań kamery. Spróbuj ponownie.');
      } else {
        toast.error(`Nie można uruchomić kamery: ${error.message || 'nieznany błąd'}`);
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0);

    const dataUrl = canvas.toDataURL('image/jpeg');
    setImagePreview(dataUrl);
    const base64 = dataUrl.split(',')[1];
    setImageBase64(base64);

    stopCamera();
  };

  const handleSubmit = async () => {
    if (!imageBase64) {
      toast.error('Wybierz lub zrób zdjęcie notatki');
      return;
    }

    setIsSaving(true);

    try {
      const noteData: HandwrittenNoteRequest = {
        meetingId,
        imageBase64,
      };

      const response = await notesApi.uploadHandwrittenNote(noteData);

      if (response.success) {
        setIsSaved(true);
        toast.success('Notatka odręczna zapisana!');

        if (onComplete) {
          onComplete(response.note.id);
        }
      }
    } catch (error) {
      toast.error('Błąd podczas zapisywania notatki');
      console.error('Handwritten note error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Czy na pewno chcesz usunąć zdjęcie?')) {
      setImagePreview(null);
      setImageBase64(null);
      setIsSaved(false);
      stopCamera();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleNewNote = () => {
    setImagePreview(null);
    setImageBase64(null);
    setIsSaved(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Notatki odręczne</h3>
        <div className="text-xs sm:text-sm text-muted-foreground">
          Zdjęcia notatek
        </div>
      </div>

      {!isSaved ? (
        <>
          {/* Image preview or camera */}
          <div className="space-y-3 sm:space-y-4">
            <div className="relative border-2 border-dashed border-border rounded-lg p-3 sm:p-8 text-center bg-card">
              {!imagePreview && !isCameraActive ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="text-3xl sm:text-6xl">✍️</div>
                  <p className="text-xs sm:text-base text-muted-foreground px-2">Wybierz lub zrób zdjęcie notatki odręcznej</p>

                  <div className="flex flex-col gap-2 justify-center px-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="w-full text-sm sm:text-base py-2 sm:py-2"
                    >
                      📁 Wybierz plik
                    </Button>

                    <Button
                      onClick={startCamera}
                      variant="outline"
                      className="w-full text-sm sm:text-base py-2 sm:py-2"
                    >
                      📷 Użyj kamery
                    </Button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : isCameraActive ? (
                <div className="space-y-3 sm:space-y-4">
                  <div className="relative w-full mx-auto overflow-visible" style={{ maxWidth: '100%' }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full rounded-lg bg-black"
                      style={{
                        minHeight: '300px',
                        maxHeight: '70vh',
                        display: 'block'
                      }}
                    />
                  </div>
                  <div className="flex flex-col gap-2 justify-center px-2">
                    <Button onClick={capturePhoto} className="w-full text-sm sm:text-base py-2 sm:py-2">
                      📸 Zrób zdjęcie
                    </Button>
                    <Button onClick={stopCamera} variant="outline" className="w-full text-sm sm:text-base py-2 sm:py-2">
                      Anuluj
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  <div className="w-full mx-auto" style={{ maxWidth: '600px' }}>
                    <img
                      src={imagePreview ?? undefined}
                      alt="Handwritten note preview"
                      className="w-full rounded-lg object-contain"
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                  <Button onClick={handleClear} variant="outline" size="sm" className="w-full sm:w-auto">
                    Zmień zdjęcie
                  </Button>
                </div>
              )}
            </div>
          </div>

          <canvas ref={canvasRef} className="hidden" />

          {/* Submit button */}
          {imagePreview && !isSaving && (
            <div className="flex flex-col gap-2 px-2">
              <Button onClick={handleSubmit} disabled={!imageBase64} className="w-full text-sm sm:text-base py-2 sm:py-2">
                {isSaving ? 'Zapisywanie...' : 'Zapisz notatkę'}
              </Button>
              <Button onClick={handleClear} variant="outline" className="w-full text-sm sm:text-base py-2 sm:py-2">
                Wyczyść
              </Button>
            </div>
          )}

          {isSaving && (
            <div className="text-center text-xs sm:text-sm text-muted-foreground px-2">
              <p>Zapisywanie notatki...</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="space-y-4">
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                Zapisana notatka
                <span className="text-xs text-accent">✓</span>
              </h4>
              <img
                src={imagePreview || ''}
                alt="Handwritten note"
                className="w-full border border-border rounded-md"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleNewNote} className="w-full sm:w-auto">Dodaj kolejną notatkę</Button>
          </div>
        </>
      )}
    </div>
  );
};
